import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Input,
  Button,
  Spin,
  Typography,
  Alert,
  List,
  Tree,
  Tabs,
  Table,
  Tag,
  Empty,
  Select,
  Space,
} from "antd";
import {
  SendOutlined,
  FileTextOutlined,
  FolderOutlined,
  SearchOutlined,
  DownloadOutlined,
  BookOutlined,
} from "@ant-design/icons";
import Layout from "../../components/Layout/Layout";
import CurriculumContent from "../../components/CurriculumContent/CurriculumContent";
import api from "../../services/api";
import "./TeacherAgents.css";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Option } = Select;
const { Search } = Input;

const CurriculumViewerAgent = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [curriculumData, setCurriculumData] = useState([]);
  const [activeTab, setActiveTab] = useState("browse");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch curriculum data
  useEffect(() => {
    const fetchCurriculumData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/curriculum", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data) {
          setCurriculumData(response.data);
        } else {
          // Mock curriculum data structure for development
          setCurriculumData(generateMockCurriculumData());
        }
      } catch (err) {
        console.error("Error fetching curriculum data:", err);
        // Use mock data in case of error
        setCurriculumData(generateMockCurriculumData());
      }
    };

    fetchCurriculumData();
  }, []);

  // Initialize WebSocket connection
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication required");
      return;
    }

    const connectWebSocket = () => {
      const ws = new WebSocket(
        `ws://localhost:8000/ws/agent/curriculum_viewer?token=${token}`
      );
      socketRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected - Curriculum Viewer Agent");
        setConnected(true);
        setError(null);

        // Send initial message
        ws.send(
          JSON.stringify({
            message: "Hello, I need help with viewing the curriculum.",
            context: "curriculum_viewing",
          })
        );
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setMessages((prev) => [
          ...prev,
          {
            text: data.message,
            sender: "agent",
            timestamp: new Date().toLocaleTimeString(),
            curricularResources: data.curricularResources || null,
          },
        ]);
        setLoading(false);

        // If search results were returned, update search results
        if (data.searchResults) {
          setSearchResults(data.searchResults);
          setActiveTab("search"); // Switch to search tab
        }
      };

      ws.onerror = (e) => {
        console.error("WebSocket error:", e);
        setError("Connection error. Please try again.");
        setConnected(false);
      };

      ws.onclose = () => {
        console.log("WebSocket disconnected");
        setConnected(false);

        // Attempt to reconnect after a delay
        setTimeout(() => {
          if (socketRef.current?.readyState === WebSocket.CLOSED) {
            connectWebSocket();
          }
        }, 3000);
      };
    };

    connectWebSocket();

    // Clean up on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, []);

  // Handle sending messages
  const handleSend = () => {
    if (
      !input.trim() ||
      !socketRef.current ||
      socketRef.current.readyState !== WebSocket.OPEN
    )
      return;

    const message = {
      text: input,
      sender: "user",
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages((prev) => [...prev, message]);
    setLoading(true);
    setInput("");

    socketRef.current.send(
      JSON.stringify({
        message: input,
        context: "curriculum_viewing",
      })
    );
  };

  // Handle pressing Enter to send message
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle search in curriculum
  const handleSearch = (value) => {
    if (!value.trim()) return;

    setLoading(true);

    // Send search request via WebSocket
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          message: `Search curriculum for: ${value}`,
          context: "curriculum_search",
          query: value,
          subject: selectedSubject,
          grade: selectedGrade,
        })
      );
    } else {
      setError("Connection error. Please try again.");
      setLoading(false);
    }
  };

  // Generate mock curriculum data for development
  const generateMockCurriculumData = () => {
    return [
      {
        title: "Science",
        key: "science",
        children: [
          {
            title: "Biology",
            key: "biology",
            children: [
              {
                title: "Grade 10",
                key: "biology-g10",
                children: [
                  {
                    title: "Cell Structure",
                    key: "biology-g10-cells",
                    isLeaf: true,
                    content: "Study of cell organelles and their functions",
                  },
                  {
                    title: "Genetics",
                    key: "biology-g10-genetics",
                    isLeaf: true,
                    content: "Study of heredity and genetic variations",
                  },
                ],
              },
              {
                title: "Grade 11",
                key: "biology-g11",
                children: [
                  {
                    title: "Human Anatomy",
                    key: "biology-g11-anatomy",
                    isLeaf: true,
                    content: "Study of human body structure and systems",
                  },
                ],
              },
            ],
          },
          {
            title: "Chemistry",
            key: "chemistry",
            children: [
              {
                title: "Grade 10",
                key: "chemistry-g10",
                children: [
                  {
                    title: "Periodic Table",
                    key: "chemistry-g10-periodic",
                    isLeaf: true,
                    content: "Study of elements and their properties",
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        title: "Mathematics",
        key: "mathematics",
        children: [
          {
            title: "Grade 10",
            key: "math-g10",
            children: [
              {
                title: "Algebra",
                key: "math-g10-algebra",
                isLeaf: true,
                content: "Study of mathematical symbols and rules",
              },
              {
                title: "Geometry",
                key: "math-g10-geometry",
                isLeaf: true,
                content: "Study of shapes, sizes, and properties of space",
              },
            ],
          },
        ],
      },
    ];
  };

  // Generate mock search results
  const generateMockSearchResults = (query) => {
    return [
      {
        title: "Cell Structure",
        subject: "Biology",
        grade: "Grade 10",
        relevance: "95%",
        content: "Study of cell organelles and their functions",
      },
      {
        title: "Cell Division",
        subject: "Biology",
        grade: "Grade 11",
        relevance: "82%",
        content: "Study of mitosis and meiosis",
      },
      {
        title: "Plant Cells",
        subject: "Biology",
        grade: "Grade 9",
        relevance: "78%",
        content: "Study of plant cell structure and function",
      },
    ];
  };

  // Handle tree node selection
  const onSelect = (selectedKeys, info) => {
    if (info.node.isLeaf) {
      const topicDetails = `Tell me about the curriculum for ${info.node.title}`;
      setInput(topicDetails);
    }
  };

  // Columns for search results table
  const searchColumns = [
    {
      title: "Topic",
      dataIndex: "title",
      key: "title",
      render: (text) => <a>{text}</a>,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Grade",
      dataIndex: "grade",
      key: "grade",
    },
    {
      title: "Relevance",
      dataIndex: "relevance",
      key: "relevance",
      render: (text) => <Tag color="green">{text}</Tag>,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            size="small"
            onClick={() => {
              setInput(
                `Tell me about the curriculum for ${record.title} in ${record.subject} for ${record.grade}`
              );
              setActiveTab("chat");
            }}
          >
            View Details
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <div className="teacher-agent-container">
        <Card
          className="teacher-agent-card"
          title={
            <div className="agent-header">
              <Title level={3}>Curriculum Viewer Assistant</Title>
              {connected ? (
                <Text type="success" className="connection-status">
                  Connected
                </Text>
              ) : (
                <Text type="danger" className="connection-status">
                  Disconnected
                </Text>
              )}
            </div>
          }
        >
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              className="error-alert"
            />
          )}

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="curriculum-tabs"
          >
            <TabPane tab="Browse Curriculum" key="browse">
              <div className="curriculum-browser">
                <div className="curriculum-filters">
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Select
                      placeholder="Select Subject"
                      style={{ width: "100%" }}
                      onChange={setSelectedSubject}
                      allowClear
                    >
                      <Option value="science">Science</Option>
                      <Option value="mathematics">Mathematics</Option>
                      <Option value="english">English</Option>
                      <Option value="social_studies">Social Studies</Option>
                    </Select>

                    <Select
                      placeholder="Select Grade"
                      style={{ width: "100%" }}
                      onChange={setSelectedGrade}
                      allowClear
                    >
                      {[...Array(12)].map((_, i) => (
                        <Option key={i + 1} value={`grade_${i + 1}`}>
                          Grade {i + 1}
                        </Option>
                      ))}
                    </Select>

                    <Search
                      placeholder="Search curriculum..."
                      onSearch={handleSearch}
                      enterButton
                    />
                  </Space>
                </div>

                <div className="curriculum-tree">
                  {curriculumData.length > 0 ? (
                    <Tree
                      showLine
                      showIcon
                      defaultExpandAll={false}
                      onSelect={onSelect}
                      treeData={curriculumData}
                      icon={({ isLeaf }) =>
                        isLeaf ? <FileTextOutlined /> : <FolderOutlined />
                      }
                    />
                  ) : (
                    <Empty description="No curriculum data available" />
                  )}
                </div>
              </div>
            </TabPane>

            <TabPane tab="Search Results" key="search">
              <div className="search-results-container">
                {searchResults.length > 0 ? (
                  <Table
                    dataSource={searchResults}
                    columns={searchColumns}
                    pagination={{ pageSize: 5 }}
                  />
                ) : (
                  <Empty description="No search results yet. Try searching in the Browse tab." />
                )}
              </div>
            </TabPane>

            <TabPane tab="Chat" key="chat">
              <div className="message-container">
                {messages.length === 0 ? (
                  <div className="welcome-message">
                    <Title level={4}>
                      Welcome to Curriculum Viewer Assistant!
                    </Title>
                    <Text>
                      I can help you navigate and understand the curriculum.
                      Browse the curriculum structure, search for specific
                      topics, or ask me questions about curriculum content.
                    </Text>
                    <Paragraph className="instructions">
                      <ul>
                        <li>
                          Find curriculum content for specific subjects and
                          grade levels
                        </li>
                        <li>
                          Get teaching resources related to curriculum topics
                        </li>
                        <li>
                          Understand curriculum objectives and learning outcomes
                        </li>
                        <li>
                          Discover connections between different curriculum
                          areas
                        </li>
                      </ul>
                    </Paragraph>
                  </div>
                ) : (
                  <List
                    itemLayout="horizontal"
                    dataSource={messages}
                    renderItem={(message) => (
                      <div
                        className={`message ${
                          message.sender === "user"
                            ? "user-message"
                            : "agent-message"
                        }`}
                      >
                        <div className="message-content">
                          <div className="message-text">{message.text}</div>
                          {message.curricularResources && (
                            <div className="curricular-resources">
                              <Card size="small" className="resource-card">
                                <Text strong>Related Resources:</Text>
                                <List
                                  size="small"
                                  dataSource={message.curricularResources}
                                  renderItem={(resource) => (
                                    <List.Item>
                                      <Space>
                                        <BookOutlined />
                                        <a href="#">{resource.title}</a>
                                        <Tag color="blue">{resource.type}</Tag>
                                      </Space>
                                    </List.Item>
                                  )}
                                />
                              </Card>
                            </div>
                          )}
                          <div className="message-timestamp">
                            {message.timestamp}
                          </div>
                        </div>
                      </div>
                    )}
                  />
                )}
                {loading && (
                  <div className="agent-message">
                    <div className="message-content">
                      <div className="loading-indicator">
                        <Spin size="small" />{" "}
                        <Text italic>Searching curriculum...</Text>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="input-area">
                <TextArea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about curriculum content, topics, or learning objectives..."
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  disabled={!connected}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSend}
                  disabled={!connected || !input.trim()}
                  className="send-button"
                >
                  Send
                </Button>
              </div>
            </TabPane>
          </Tabs>
        </Card>
      </div>
    </Layout>
  );
};

export default CurriculumViewerAgent;
